import { useQuery } from "@tanstack/react-query";
import { withNamespaces } from "react-i18next";
import { toast } from "react-toastify";
import { Card, CardBody, CardText, CardTitle, Col, PopoverBody, PopoverHeader, Row, UncontrolledPopover } from "reactstrap";
import SupplierProductService from "src/modules/3rd-party-management/apis/common/SupplierProductService";
import { useEffect, useMemo, useState } from "react";
import GeoMap from "src/modules/3rd-party-management/components/geo-map";
import ReactApexChart from "react-apexcharts";
import Header from "./components/Header";
import Summary from "./components/Summary";
import CoverageCard from "./components/CoverageCard";
import ForestChangeChart from "./components/ForestChangeChart";
import RiskAssessmentFlow from "./components/RiskAssessmentFlow";
import { center } from '@turf/turf';
import OpenStreetMapService from "src/modules/3rd-party-management/apis/common/OpenStreetMapService";
import GeojsonViewer from "src/modules/3rd-party-management/components/geo-map/components/GeojsonViewer";
const API_BASE = "https://geocledian.com";

const absolutize = (url) => {
    if (!url) return null;
    if (url.startsWith("http")) return url;
    return `${API_BASE}/agknow/api/v4${url}`;
}

const GeoCledianDetails = ({ t, productId, supplierId }) => {
    const [ plotInfos, setPlotInfos ] = useState({});

    const {
        isFetching: geoResultIsFetching,
        data: geoData
    } = useQuery({
        queryKey: [
            '3rd-party-management-eudr-product-geo-data',
            supplierId,
            productId
        ],
        queryFn: async () => {
            const service = SupplierProductService.getInstance();

            return await service.fetchGeoCledianResult(supplierId, {
                product_id: productId
            });
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: () => {
            toast(t('An error occurred while fetching product details.'), {
                type: 'error'
            });
        }
    });

    const coords = useMemo(() => {
        if (geoData?.geometry) {
            return {
                type: 'FeatureCollection',
                features: [
                    {
                        geometry: geoData.geometry,
                        type: 'Feature',
                        properties: {}
                    }
                ]
            }
        }

        return null;
    }, [ geoData ]);

    useEffect(() => {
        if (coords) {
            const polygonCenter = center(coords.features[0]);
            const service = OpenStreetMapService.getInstance();

            service.getPointAddress(
                polygonCenter.geometry.coordinates[0],
                polygonCenter.geometry.coordinates[1] 
            ).then(({ data }) => {
                setPlotInfos({
                    center: {
                        lng: polygonCenter.geometry.coordinates[0],
                        lat: polygonCenter.geometry.coordinates[1]
                    },
                    address: data.display_name
                })
            });
        }
    }, [ coords ]);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    if (geoResultIsFetching) return `${t('Loading')}...`;

    const totalArea = Number(geoData.area);
    
    const forestCover = Number(geoData.landcover_forest_coverage);
    const plantationCover = Number(geoData.landcover_forest_coverage);
    const shrubsCover = Number(geoData.landcover_shrubs_coverage);
    const noTreeCover = Number(geoData.landcover_no_trees_coverage);

    const deforestationCheckNotNeeded = (geoData.country_risk === 'low' && geoData.commodity !== 'wood');

    return (
        <div className="product-geo-details-page">
            <Row>
                <Col sm='12' lg="8">
                    <Header 
                        geoData={geoData}
                    />
                </Col>

                <Col sm="12" lg="4">
                    <Summary 
                        geoData={geoData}
                    />
                </Col>
                
                {coords && (
                    <Col sm="12" md="12" lg="6" className="mb-4">
                        <Card className="h-100">
                            <CardBody>
                                <div className="mb-1 d-flex flex-column">
                                    <p className="mb-1">
                                        <strong class="me-2">
                                            {t('Production Area')} (ha):
                                        </strong>
                                        <span>
                                            {totalArea}
                                        </span>
                                    </p>
                                    <p className="mb-1">
                                        <strong class="me-2">
                                            {t('Address')}:
                                        </strong>
                                        <span>
                                            {plotInfos.address || t('N/A')}
                                        </span>
                                    </p>
                                    <p className="mb-1">
                                        {plotInfos.center ? (
                                            <>
                                                <strong class="me-2">
                                                    {t('Coordinates')}:
                                                </strong>
                                                <span>
                                                    {`(${plotInfos.center.lat.toFixed(6)}, ${plotInfos.center.lng.toFixed(6)})`}
                                                </span>
                                            </>
                                        ) : t('Coordinates not available')}
                                    </p>

                                    <GeojsonViewer 
                                        geojsonData={coords}
                                    />
                                </div>
                                <GeoMap
                                    readOnly={true}
                                    preDefinedGeo={coords}
                                    multiDraw={true}
                                    styles={{
                                        height: '320px'
                                    }}
                                />
                            </CardBody>
                        </Card>
                    </Col>
                )}

                <Col sm="12" md="12" lg="6" className="mb-4">
                    <RiskAssessmentFlow 
                        deforestationCheckNotNeeded={deforestationCheckNotNeeded}
                    />
                </Col>

                {!deforestationCheckNotNeeded && (
                    <>
                        <Col sm='12' md="3">
                            <CoverageCard
                                title="Forest Coverage"
                                value={totalArea * forestCover}
                                percent={forestCover * 100}
                                description="Area covered by natural forest"
                            />
                        </Col>

                        <Col sm='12' md="3">
                            <CoverageCard
                                title="Plantation Coverage"
                                value={totalArea * plantationCover}
                                percent={plantationCover * 100}
                                description="Area with planted crops or trees"
                            />
                        </Col>

                        <Col sm='12' md="3">
                            <CoverageCard
                                title="Shrubs Coverage"
                                value={totalArea * shrubsCover}
                                percent={shrubsCover * 100}
                                description="Area covered by shrubs or small vegetation"
                            />
                        </Col>

                        <Col sm='12' md="3">
                            <CoverageCard
                                title="No Trees"
                                value={totalArea * noTreeCover}
                                percent={noTreeCover * 100}
                                description="Area with no tree cover or bare land"
                            />
                        </Col>

                        <Col sm="12" md="12" lg="12" xl="4">
                            <Card>
                                <CardBody>
                                    <CardTitle tag="h5" className="mb-3">
                                        <span className="me-1">
                                            {t('Land Cover Composition')}
                                        </span>
                                        <i
                                            className="ri-information-line"
                                            style={{
                                                cursor: 'pointer'
                                            }}
                                            id="land-cover-composition-help-text"
                                        />

                                        <UncontrolledPopover
                                            trigger="hover"
                                            target="land-cover-composition-help-text"
                                        >
                                            <PopoverHeader>
                                                {t('Land Cover Composition')}
                                            </PopoverHeader>
                                            <PopoverBody>
                                                <p>
                                                    {t('This chart shows the proportion of different land cover types within the selected parcel.')}
                                                </p>
                                                <ul>
                                                    <li>
                                                        <strong>
                                                            {t('Forest Coverage')}:
                                                        </strong>
                                                        {t('Areas with natural forest.')}
                                                    </li>
                                                    <li>
                                                        <strong>
                                                            {t('Plantation Coverage')}:
                                                        </strong>
                                                        {t('Areas used for agricultural plantations (e.g., soy, coffee, palm).')}
                                                    </li>
                                                    <li>
                                                        <strong>
                                                            {t('Shrubs Coverage')}:
                                                        </strong>
                                                        {t('Areas dominated by shrubs or low vegetation.')}
                                                    </li>
                                                    <li>
                                                        <strong>
                                                            {t('No Trees')}:
                                                        </strong>
                                                        {t('Open land with no tree or shrub cover.')}
                                                    </li>
                                                </ul>
                                                <p>
                                                    {t('Use this chart to quickly understand how much of the parcel is natural forest versus agricultural or open land, which is critical for assessing deforestation risk under EUDR compliance.')}
                                                </p>
                                            </PopoverBody>
                                        </UncontrolledPopover>
                                    </CardTitle>
                                    <ReactApexChart
                                        options={{
                                            labels: [
                                                t('Forest Coverage'),
                                                t('Plantation Coverage'),
                                                t('Shrubs Coverage'),
                                                t('No Trees')
                                            ],
                                            plotOptions: {
                                                pie: {
                                                    donut: {
                                                        size: "75%",
                                                    }
                                                }
                                            },
                                            legend: {
                                                show: true,
                                                position: 'bottom'
                                            },
                                            tooltip: {
                                                y: {
                                                    formatter: (val) => `${(val * 100).toFixed(2)}%`,
                                                }
                                            },
                                            colors: [
                                                '#6aa84f',
                                                '#a0a0a0',
                                                '#f9d57c',
                                                '#e3e3e3'
                                            ]
                                        }}
                                        series={[
                                            Number(geoData.landcover_forest_coverage),
                                            Number(geoData.landcover_plantation_coverage),
                                            Number(geoData.landcover_shrubs_coverage),
                                            Number(geoData.landcover_no_trees_coverage)
                                        ]}
                                        type="pie"
                                    />
                                </CardBody>
                            </Card>
                        </Col>

                        <Col sm="12" md="12" lg="12" xl="8">
                            <ForestChangeChart
                                geoData={geoData}
                            />
                        </Col>

                        <Col sm='12' md='6'>
                            <Card>
                                <Row className="no-gutters align-items-center">
                                    <Col sm="4" className="p-4">
                                        <img
                                            src={absolutize(`${geoData.rgb_png_baseline_date}?key=c4287ef1-9373-42b7-a9c0-ae34f53c5e39`)}
                                            alt="Baseline"
                                            className="img-fluid rounded"
                                            style={{
                                                maxWidth: '100%'
                                            }}
                                        />
                                    </Col>

                                    <Col sm="8">
                                        <CardBody>
                                            <CardTitle tag="h5">
                                                {t('Production Start Image')}
                                            </CardTitle>
                                            <CardText>
                                                {t('Satellite image captured at the beginning of the production period, showing the initial land cover and vegetation conditions used as a baseline for deforestation assessment.')}
                                            </CardText>
                                        </CardBody>
                                    </Col>
                                </Row>
                            </Card>
                        </Col>

                        <Col sm='12' md='6'>
                            <Card>
                                <Row className="no-gutters align-items-center">
                                    <Col sm="4" className="p-4">
                                        <img
                                            src={absolutize(`${geoData.landcover_png_baseline_date}?key=c4287ef1-9373-42b7-a9c0-ae34f53c5e39`)}
                                            alt="Landcover Mask"
                                            className="img-fluid rounded"
                                            style={{
                                                maxWidth: '100%'
                                            }}
                                        />
                                    </Col>

                                    <Col sm="8">
                                        <CardBody>
                                            <CardTitle tag="h5">
                                                {t('Land Cover')}
                                            </CardTitle>
                                            <CardText>
                                                {t('Satellite-based classification of the area’s surface features, showing how much of the land is covered by vegetation, crops, forests, or other types of terrain.')}
                                            </CardText>
                                        </CardBody>
                                    </Col>
                                </Row>
                            </Card>
                        </Col>

                        <Col sm='12' md='6'>
                            <Card>
                                <Row className="no-gutters align-items-center">
                                    <Col sm="4" className="p-4">
                                        <img
                                            src={absolutize(`${geoData.rgb_png_production_enddate}?key=c4287ef1-9373-42b7-a9c0-ae34f53c5e39`)}
                                            alt="Production End"
                                            className="img-fluid rounded"
                                            style={{
                                                maxWidth: '100%'
                                            }}
                                        />
                                    </Col>

                                    <Col sm="8">
                                        <CardBody>
                                            <CardTitle tag="h5">
                                                {t('Production End Image')}
                                            </CardTitle>
                                            <CardText>
                                                {t('Satellite image captured at the end of the production period, showing the land’s condition after production activities to identify any changes in vegetation or forest cover.')}
                                            </CardText>
                                        </CardBody>
                                    </Col>
                                </Row>
                            </Card>
                        </Col>

                        <Col sm='12' md='6'>
                            <Card>
                                <Row className="no-gutters align-items-center">
                                    <Col sm="4" className="p-4">
                                        <img
                                            src={absolutize(`${geoData.forest_change_png}?key=c4287ef1-9373-42b7-a9c0-ae34f53c5e39`)}
                                            alt="Forest Change"
                                            className="img-fluid rounded"
                                            style={{
                                                maxWidth: '100%'
                                            }}
                                        />
                                    </Col>

                                    <Col sm="8">
                                        <CardBody>
                                            <CardTitle tag="h5">
                                                {t('Forest Disturbance')}
                                            </CardTitle>
                                            <CardText>
                                                {t('Visualization of areas where forest cover has been altered or lost, highlighting deforestation or degradation events during the production period.')}
                                            </CardText>
                                        </CardBody>
                                    </Col>
                                </Row>
                            </Card>
                        </Col>
                    </>
                )}
            </Row>
        </div>
    )
}

export default withNamespaces()(
    GeoCledianDetails
);